import { useTheme } from '@/context/ThemeContext';
import type { Lang } from '@/pages/LandingPage';

interface PublicPageShellProps {
  title: string;
  lang: Lang;
  setLang: (lang: Lang) => void;
  onBack: () => void;
  children: React.ReactNode;
}

export function PublicPageShell({ title, lang, setLang, onBack, children }: PublicPageShellProps) {
  const { theme, setTheme, resolvedTheme } = useTheme();

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <header className="border-b border-border bg-background/95 backdrop-blur-[12px] sticky top-0 z-50">
        <div className="max-w-[1080px] mx-auto px-4 md:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="text-sm text-muted-foreground hover:text-foreground transition">
              ← {lang === 'zh' ? '返回首页' : 'Back to home'}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setLang(lang === 'zh' ? 'en' : 'zh')}
              className="px-2 py-1 text-xs font-medium rounded-md border border-border hover:bg-secondary transition"
            >
              {lang === 'zh' ? 'EN' : '中'}
            </button>
            <button
              onClick={() => setTheme(theme === 'dark' ? 'light' : theme === 'light' ? 'system' : 'dark')}
              className="p-2 rounded-md hover:bg-secondary text-muted-foreground transition"
              title="切换主题"
            >
              {resolvedTheme === 'dark' ? (
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" /></svg>
              ) : (
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" /></svg>
              )}
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <div className="max-w-[1080px] mx-auto px-4 md:px-6 py-12 md:py-16">
          <h1 className="text-3xl md:text-4xl font-semibold tracking-[-0.02em] mb-8">{title}</h1>
          <div className="prose prose-neutral dark:prose-invert max-w-none">{children}</div>
        </div>
      </main>

      <footer className="border-t border-border bg-card py-6">
        <div className="max-w-[1080px] mx-auto px-4 md:px-6 space-y-2 text-xs text-muted-foreground">
          <div className="flex flex-col md:flex-row items-center justify-between gap-3">
            <div>© {new Date().getFullYear()} {lang === 'zh' ? '东莞市沐辰日用品有限公司' : 'Dongguan Muchen Daily Products Co., Ltd.'}. {lang === 'zh' ? '版权所有' : 'All rights reserved'}.</div>
            <a
              href="https://beian.miit.gov.cn/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-foreground transition"
            >
              粤ICP备2023146695号-3
            </a>
          </div>
          <p className="text-center md:text-left leading-relaxed">
            {lang === 'zh'
              ? '听阅 TingYue 为本公司自主研发的财务数据分析与资产管理系统，服务于公司自有资金的投资研究与内部管理'
              : 'TingYue is a financial data analysis and asset management system developed in-house by the company, serving its proprietary investment research and internal management'}
          </p>
        </div>
      </footer>
    </div>
  );
}
